import { Button, Divider } from '@vrplatform/components';
import { MantineReactTable, useLazyTable } from '@vrplatform/table';
import { hasValue } from '@vrplatform/utils';
import { Box, Group, useMantineTheme } from '@mantine/core';
import type { BalanceSheetJournalEntriesArgs } from './BalanceSheetTableCell';
import { BalanceSheetTableFilter } from './BalanceSheetTableFilter';
import { useBalanceSheetTableQuery } from './useBalanceSheetTableQuery';
import { BALANCE_SHEET_MAX_DEPTH } from './_utils';
import { useBalanceSheetTableColumns } from './useBalanceSheetTableColumns';

export const BalanceSheetTable = ({
  onOpenJournalEntries,
}: {
  onOpenJournalEntries?: (args: BalanceSheetJournalEntriesArgs) => void;
}) => {
  const queryData = useBalanceSheetTableQuery();
  const rows = queryData.data?.rows ?? [];

  const theme = useMantineTheme();

  const columns = useBalanceSheetTableColumns(
    queryData.data?.columns ?? [],
    queryData.data?.rows ?? [],
    onOpenJournalEntries
  );

  const table = useLazyTable({
    data: {
      error: queryData.error?.message,
      loading: queryData.isLoading,
      rows,
      rowCount: rows.length,
    },
    table: {
      columns,
      hideTopBar: true,
      hidePagination: true,
      columnOrder: columns
        .map((c) => c.id || c.accessorKey || c.header)
        .filter(hasValue),
    },
    subRows: {
      getRowCanExpand: (row) => (row.subRows?.length ?? 0) > 0,
      getSubRows: (row) => row.subRows,
      defaultExpanded: true,
      columnWidth: 60,
      allowExpandingAll: false,
      getRowBackgroundColor: (row) => {
        const bg = theme.colors.gray[BALANCE_SHEET_MAX_DEPTH - row.depth];

        if (bg)
          return `${theme.colors.gray[BALANCE_SHEET_MAX_DEPTH - row.depth]}!important`;

        return `${theme.white}!important`;
      },
    },
    pinned: ['mrt-row-expand', 'title'],
  });

  return (
    <Box
      sx={{
        '--mrt-pinned-column-background-color': 'initial',
        '--table-highlight-on-hover-color': 'initial',
        '--mrt-row-hover-background-color': 'initial',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group justify="space-between">
        <Group>
          <BalanceSheetTableFilter.Date />
          <BalanceSheetTableFilter.Book />
          <Divider orientation="vertical" />
          <BalanceSheetTableFilter.View />
        </Group>

        <Group>
          <BalanceSheetTableFilter.Type />

          <Divider orientation="vertical" />

          <Button
            variant="transparent"
            leftIcon="CollapseIcon"
            leftIconSize={14}
            onClick={() => table.toggleAllRowsExpanded()}
          >
            Collapse all
          </Button>
        </Group>
      </Group>
      <Divider mt="sm" />
      <MantineReactTable table={table} />
    </Box>
  );
};
